import React, { useState } from 'react';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WorkspaceSidebar from './WorkspaceSidebar';
import WorkspaceTopBar from './WorkspaceTopBar';
import { getWorkspaceTokens, WORKSPACE_TOP_BAR_MIN_HEIGHT } from './workspaceStyles';
import {
  ANALYTICS_HEADER_HEIGHT,
  ANALYTICS_SIDEBAR_CONTENT_INSET,
} from '../../layout/headerChrome';

function AnalyticsWorkspaceShell({
  title,
  description,
  badge,
  icon,
  sidebarItems,
  sidebarGroups,
  sidebarHeaderActions,
  sidebarActions,
  expandActiveSectionOnly = false,
  activeSectionId,
  activeViewId,
  activeNavigationItemId,
  activeNavigationLevelId,
  activeNavigationViewId,
  activeItemAction,
  chips,
  depthLinks,
  headerAccessory,
  headerActions,
  viewSection,
  viewSectionLabel,
  filtersOpen,
  onToggleFilters,
  filterHostRef,
  children,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const isMobile = useMediaQuery('(max-width:900px)');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const sidebar = (
    <WorkspaceSidebar
      title={title}
      description={description}
      badge={badge}
      icon={icon}
      items={sidebarGroups || sidebarItems}
      headerActions={sidebarHeaderActions}
      actions={sidebarActions}
      expandActiveSectionOnly={expandActiveSectionOnly}
      activeSectionId={activeSectionId}
      activeViewId={activeViewId}
      activeNavigationItemId={activeNavigationItemId}
      activeNavigationLevelId={activeNavigationLevelId}
      activeNavigationViewId={activeNavigationViewId}
      activeItemAction={activeItemAction}
      topInset={!isMobile ? ANALYTICS_SIDEBAR_CONTENT_INSET : 0}
      headerMinHeight={!isMobile
        ? ANALYTICS_HEADER_HEIGHT + WORKSPACE_TOP_BAR_MIN_HEIGHT - ANALYTICS_SIDEBAR_CONTENT_INSET
        : 0}
      collapsed={!isMobile && sidebarCollapsed}
      onToggleCollapsed={!isMobile ? () => setSidebarCollapsed((value) => !value) : undefined}
      onNavigate={() => setMobileNavOpen(false)}
    />
  );

  return (
    <Box data-testid="analytics-workspace-shell" sx={{
      height: isMobile ? `calc(100svh - ${ANALYTICS_HEADER_HEIGHT}px)` : '100svh',
      minHeight: 0,
      maxHeight: isMobile ? `calc(100svh - ${ANALYTICS_HEADER_HEIGHT}px)` : '100svh',
      mt: isMobile ? 0 : `calc(-${ANALYTICS_HEADER_HEIGHT}px - env(safe-area-inset-top))`,
      display: 'flex',
      alignItems: 'stretch',
      overflow: 'hidden',
      background: isMobile
        ? tokens.surface
        : `linear-gradient(to bottom, ${tokens.toolbar} 0 ${ANALYTICS_HEADER_HEIGHT}px, ${tokens.surface} ${ANALYTICS_HEADER_HEIGHT}px)`,
    }}>
      {!isMobile && sidebar}
      {isMobile && (
        <Drawer
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { backgroundColor: tokens.rail } }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box sx={{
        minWidth: 0,
        minHeight: 0,
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pt: !isMobile ? `${ANALYTICS_HEADER_HEIGHT}px` : 0,
      }} data-testid="analytics-workspace-main">
        <WorkspaceTopBar
          title={title}
          icon={icon}
          chips={chips}
          depthLinks={depthLinks}
          headerAccessory={headerAccessory}
          headerActions={headerActions}
          viewSection={viewSection}
          viewSectionLabel={viewSectionLabel}
          isMobile={isMobile}
          onOpenNav={() => setMobileNavOpen(true)}
          filtersOpen={filtersOpen}
          onToggleFilters={onToggleFilters}
          filterHostRef={filterHostRef}
        />
        {children}
      </Box>
    </Box>
  );
}

export default AnalyticsWorkspaceShell;
